import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FaFilter } from 'react-icons/fa';
import UiSelect from '../components/UiSelect.jsx';
import { getReport, exportReport, getRooms, getSeatsData, getStudentsSummary } from '../services/api';
import axios from '../api';
import '../styles/pages/reports.css';

const extractList = (response, keys = []) => {
  const containers = [response, response?.data, response?.data?.data, response?.result];
  for (const container of containers) {
    if (Array.isArray(container)) return container;
    for (const key of keys) {
      if (Array.isArray(container?.[key])) return container[key];
    }
  }
  return [];
};

const FALLBACK_REPORT_TYPES = [
  {value:'revenue',label:'Revenue Report',requiresDates:false,dateOptional:true},
  {value:'occupancy',label:'Seat Occupancy',requiresDates:false,dateOptional:false},
  {value:'subscriptions',label:'Subscription Expiry',requiresDates:true},
  {value:'payments',label:'Payment History',requiresDates:true},
  {value:'students',label:'Student Summary',requiresDates:true}
];

function ReportsSkeleton() {
  return (
    <div className="reports-skeleton" aria-label="Loading report data" aria-busy="true">
      <div className="reports-skeleton-cards">
        {[1, 2, 3, 4].map(item => <div className="reports-skeleton-card" key={item}><span /><b /><small /></div>)}
      </div>
      <div className="reports-skeleton-charts">
        {[1, 2].map(item => <div className="reports-skeleton-chart" key={item}><span /><div /></div>)}
      </div>
      <div className="reports-skeleton-table">
        <span />
        {[1, 2, 3, 4].map(item => <div key={item}><i /><i /><i /><i /></div>)}
      </div>
    </div>
  );
}

export function ReportsPage({toast}){
  const [searchParams,setSearchParams] = useSearchParams();
  const initialParams = searchParams;
  const [filters,setFilters] = useState(()=>({
    library:initialParams.get('library') || '',
    start:initialParams.get('start') || '',
    end:initialParams.get('end') || ''
  }));
  const [reportType, setReportType] = useState(()=>initialParams.get('type') || 'revenue');
  const [search, setSearch] = useState(()=>initialParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(()=>initialParams.get('status') || '');
  const [sortKey, setSortKey] = useState(()=>initialParams.get('sort') || '');
  const [sortOrder, setSortOrder] = useState(()=>initialParams.get('order') === 'asc' ? 'asc' : 'desc');
  const [rangeMode, setRangeMode] = useState(()=>initialParams.get('range') || 'all');
  const [rawPayments,setRawPayments]=useState([]);
  const [reportSummary,setReportSummary]=useState({});
  const [reportColumns,setReportColumns]=useState([]);
  const [hiddenColumns,setHiddenColumns]=useState([]);
  const [libs,setLibs]=useState([]);
  const [rooms,setRooms]=useState([]);
  const [seats,setSeats]=useState([]);
  const [students,setStudents]=useState([]);
  const [librarySnapshotLoading,setLibrarySnapshotLoading]=useState(true);
  const [librarySnapshotError,setLibrarySnapshotError]=useState(false);
  const [loading,setLoading]=useState(true);
  const [generating,setGenerating]=useState(false);
  const [showExport,setShowExport]=useState(false);
  const [filtersOpen,setFiltersOpen]=useState(false);
  const [reportError,setReportError]=useState('');
  const [catalog,setCatalog]=useState(null);
  const [currentPage,setCurrentPage]=useState(()=>{
    const page=Number(initialParams.get('page'));
    return Number.isInteger(page) && page>0 ? page : 1;
  });
  const [pageSize,setPageSize]=useState(()=>{
    const size=Number(initialParams.get('pageSize'));
    return Number.isInteger(size) && size>=1 && size<=100 ? size : 10;
  });
  const [pagination,setPagination]=useState({totalRecords:0,totalPages:1});
  const requestId=useRef(0);
  const lastSyncedState=useRef('');

  const reportTypes=catalog?.reportTypes?.length ? catalog.reportTypes : FALLBACK_REPORT_TYPES;
  const activeSpec=reportTypes.find(type=>type.value===reportType);
  const requiresDates=activeSpec ? Boolean(activeSpec.requiresDates) : reportType!=='revenue';
  const dateOptional=activeSpec ? Boolean(activeSpec.dateOptional) : reportType==='revenue';
  const statusOptions=useMemo(()=>activeSpec ? activeSpec.statuses || [] : ['paid','completed','pending','due','active','expired','reserved'],[activeSpec]);
  const sortOptions=useMemo(()=>activeSpec ? activeSpec.sorts || [] : ['payment_date','created_at','amount','student_name','library_name','status'],[activeSpec]);

  // Keep filter state in sync with the URL (back/forward + shareable links).
  useEffect(()=>{
    const nextType=searchParams.get('type') || 'revenue';
    const nextSearch=searchParams.get('search') || '';
    const nextLibrary=searchParams.get('library') || '';
    const nextStart=searchParams.get('start') || '';
    const nextEnd=searchParams.get('end') || '';
    const nextStatus=searchParams.get('status') || '';
    const nextSort=searchParams.get('sort') || '';
    const nextOrder=searchParams.get('order') === 'asc' ? 'asc' : 'desc';
    const nextRange=searchParams.get('range') || 'all';
    const pageParam=Number(searchParams.get('page'));
    const nextPage=Number.isInteger(pageParam) && pageParam>0 ? pageParam : 1;
    const sizeParam=Number(searchParams.get('pageSize'));
    const nextPageSize=Number.isInteger(sizeParam) && sizeParam>=1 && sizeParam<=100 ? sizeParam : 10;

    setReportType(previous=>previous===nextType ? previous : nextType);
    setSearch(previous=>previous===nextSearch ? previous : nextSearch);
    setStatusFilter(previous=>previous===nextStatus ? previous : nextStatus);
    setSortKey(previous=>previous===nextSort ? previous : nextSort);
    setSortOrder(previous=>previous===nextOrder ? previous : nextOrder);
    setRangeMode(previous=>previous===nextRange ? previous : nextRange);
    setCurrentPage(previous=>previous===nextPage ? previous : nextPage);
    setPageSize(previous=>previous===nextPageSize ? previous : nextPageSize);
    setFilters(previous=>previous.library===nextLibrary && previous.start===nextStart && previous.end===nextEnd
      ? previous
      : {library:nextLibrary,start:nextStart,end:nextEnd});
  },[searchParams]);

  useEffect(()=>{
    const snapshot=JSON.stringify([reportType,search,statusFilter,sortKey,sortOrder,rangeMode,currentPage,pageSize,filters.library,filters.start,filters.end]);
    if(snapshot===lastSyncedState.current) return;
    lastSyncedState.current=snapshot;
    const next=new URLSearchParams();
    if(reportType && reportType!=='revenue') next.set('type',reportType);
    if(search) next.set('search',search);
    if(filters.library) next.set('library',filters.library);
    if(filters.start) next.set('start',filters.start);
    if(filters.end) next.set('end',filters.end);
    if(statusFilter) next.set('status',statusFilter);
    if(sortKey) next.set('sort',sortKey);
    if(sortKey && sortOrder!=='desc') next.set('order',sortOrder);
    if(currentPage>1) next.set('page',String(currentPage));
    if(pageSize!==10) next.set('pageSize',String(pageSize));
    if(rangeMode && rangeMode!=='all') next.set('range',rangeMode);
    if(next.toString()!==searchParams.toString()) setSearchParams(next,{replace:true});
  },[reportType,search,statusFilter,sortKey,sortOrder,rangeMode,currentPage,pageSize,filters.library,filters.start,filters.end,searchParams,setSearchParams]);

  const buildReportParams = (page=currentPage) => {
    const params={
      page,
      pageSize,
      search:search.trim() || undefined,
      status:statusFilter || undefined,
      sort:sortKey || undefined,
      order:sortKey ? sortOrder : undefined
    };
    if(requiresDates || (dateOptional && filters.start && filters.end)){
      if(filters.start) params.start=filters.start;
      if(filters.end) params.end=filters.end;
    }
    if(filters.library) params.library_id=filters.library;
    return params;
  };

  const fetchAll = async (params, page=currentPage) => {
    const currentRequest=++requestId.current;
    const isInitialLoad=loading && rawPayments.length===0;
    if(isInitialLoad) setLoading(true);
    else setGenerating(true);
    const requestParams = {...params,page,pageSize};
    if(requiresDates && (!filters.start || !filters.end)){
      setLoading(false);
      setGenerating(false);
      setReportError('Choose both a start and end date for this report.');
      return false;
    }

    try {
      const reportResult=await getReport(reportType,requestParams);
      if(currentRequest!==requestId.current) return false;
      const reportRes=reportResult;
      if(!reportRes || typeof reportRes!=='object') throw new Error('The report response was empty or invalid.');
      const payments=extractList(reportRes,[reportType,'rows','items','payments','students']);
      const summaryValue=reportRes.summary || reportRes.data?.summary || reportRes.data?.data?.summary;
      const summary=summaryValue && typeof summaryValue==='object' && !Array.isArray(summaryValue) ? summaryValue : {};
      setRawPayments(payments);
      setReportSummary(summary);
      setReportColumns(Array.isArray(reportRes.columns) ? reportRes.columns : Array.isArray(reportRes.data?.columns) ? reportRes.data.columns : []);
      setHiddenColumns(Array.isArray(reportRes.hiddenColumns) ? reportRes.hiddenColumns : Array.isArray(reportRes.data?.hiddenColumns) ? reportRes.data.hiddenColumns : []);
      const reportPagination=reportRes.pagination || reportRes.data?.pagination || {};
      const totalReportRecords=Number(reportPagination.totalRecords ?? reportPagination.total_records ?? payments.length);
      setPagination({
        totalRecords:Number.isFinite(totalReportRecords) ? Math.max(0,totalReportRecords) : payments.length,
        totalPages:Math.max(1,Number(reportPagination.totalPages ?? reportPagination.total_pages ?? Math.ceil(totalReportRecords/pageSize))||1)
      });

      setReportError('');
      return true;
    } catch(error) {
      if(currentRequest===requestId.current){
        console.error('Failed to fetch report data:',error);
        const message=String(error?.response?.data?.error || error?.error || error?.message || 'Could not load this report. Please try again.');
        setReportError(message);
        if(typeof toast==='function') toast(message);
      }
      return false;
    } finally {
      if(currentRequest===requestId.current){
        setLoading(false);
        setGenerating(false);
      }
    }
  };

  // Match the legacy reports page: apply filters and pagination as they change.
  useEffect(()=>{
    if(requiresDates && (!filters.start || !filters.end)) return undefined;
    const timer=window.setTimeout(()=>fetchAll(buildReportParams(currentPage),currentPage),250);
    return ()=>window.clearTimeout(timer);
  },[reportType,filters.library,filters.start,filters.end,search,statusFilter,sortKey,sortOrder,currentPage,pageSize,requiresDates]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(()=>{
    let cancelled=false;
    axios.get('/api/reports/catalog').then(response=>{
      if(!cancelled) setCatalog(response.data?.data || response.data || null);
    }).catch(error=>console.error('Failed to load report catalog:',error));
    return ()=>{cancelled=true;};
  },[]);

  useEffect(()=>{
    let cancelled=false;
    Promise.allSettled([
      axios.get('/api/libraries',{params:{include_deleted:1}}),
      getRooms(),
      getSeatsData(),
      getStudentsSummary()
    ]).then(([librariesResult,roomsResult,seatsResult,studentsResult])=>{
      if(cancelled) return;
      if(librariesResult.status==='fulfilled') setLibs(extractList(librariesResult.value.data,['libraries']));
      else console.warn('Failed to load report library options:',librariesResult.reason);
      const roomList=roomsResult.status==='fulfilled' ? extractList(roomsResult.value,['rooms']) : [];
      if(roomsResult.status==='rejected') console.warn('Failed to load report room data:',roomsResult.reason);
      if(seatsResult.status==='fulfilled'){
        const seatData=seatsResult.value;
        setSeats(extractList(seatData,['seats']));
        setRooms(roomList.length ? roomList : extractList(seatData,['rooms']));
      } else {
        console.warn('Failed to load report seat data:',seatsResult.reason);
        setRooms(roomList);
      }
      if(studentsResult.status==='fulfilled') setStudents(extractList(studentsResult.value,['students']));
      else console.warn('Failed to load report student counts:',studentsResult.reason);
      setLibrarySnapshotError(librariesResult.status==='rejected'||roomsResult.status==='rejected'||studentsResult.status==='rejected');
      setLibrarySnapshotLoading(false);
    });
    return ()=>{cancelled=true;};
  },[]);

  useEffect(()=>{
    if(!activeSpec?.requiresDates || (filters.start && filters.end)) return;
    const end=new Date();
    const start=new Date();
    start.setDate(end.getDate()-30);
    setFilters(previous=>({...previous,start:start.toISOString().split('T')[0],end:end.toISOString().split('T')[0]}));
    setRangeMode('30');
  },[activeSpec,filters.start,filters.end]);

  useEffect(()=>{
    if(statusFilter && !statusOptions.includes(statusFilter)) setStatusFilter('');
    if(sortKey && !sortOptions.includes(sortKey)) setSortKey('');
  },[statusOptions,sortOptions,statusFilter,sortKey]);

  const applyQuickRange = (mode) => {
    setRangeMode(mode);
    if (mode === 'all') { setFilters(prev => ({ ...prev, start: '', end: '' })); return; }
    if (!mode) return;
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - Number(mode));
    setFilters(prev => ({ ...prev, start: start.toISOString().split('T')[0], end: end.toISOString().split('T')[0] }));
  };

  const handleGenerate = async()=>{
    if(currentPage!==1){
      setCurrentPage(1);
      return;
    }
    const succeeded=await fetchAll(buildReportParams(1),1);
    if(succeeded && typeof toast==='function') toast('Report generated');
  };

  const handlePageChange=page=>{
    if(page<1 || page>pagination.totalPages || page===currentPage) return;
    setCurrentPage(page);
  };

  const handlePageSizeChange=event=>{
    const nextSize=Number(event.target.value);
    if(!Number.isInteger(nextSize)||nextSize<1||nextSize>100) return;
    setPageSize(nextSize);
    setCurrentPage(1);
  };

  const handleExport = async (format) => {
    try {
      const params = {...buildReportParams(1),format};
      delete params.page;
      delete params.pageSize;
      const blob = await exportReport(reportType, params);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `report-${reportType}.${format}`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      window.setTimeout(()=>URL.revokeObjectURL(url),1000);
      setShowExport(false);
    } catch (error) {
      console.error('Report export failed:', error);
      if (typeof toast === 'function') toast('Failed to export report');
    }
  };

  const filtered = useMemo(() => {
    return rawPayments;
  }, [rawPayments]);

  // LIVE CHART DATA FROM API - NO DUMMY
  const { revenueData, studentData, libraryList } = useMemo(()=>{
    const completeData=pagination.totalRecords<=rawPayments.length;
    const start=filters.start ? new Date(`${filters.start}T00:00:00`) : new Date();
    const end=filters.end ? new Date(`${filters.end}T00:00:00`) : new Date();
    if(!filters.start) start.setMonth(start.getMonth()-5);
    start.setDate(1);
    end.setDate(1);
    const monthKeys=[];
    const cursor=new Date(start);
    while(cursor<=end && monthKeys.length<120){
      monthKeys.push(`${cursor.getFullYear()}-${String(cursor.getMonth()+1).padStart(2,'0')}`);
      cursor.setMonth(cursor.getMonth()+1);
    }
    if(!monthKeys.length){
      const current=new Date();
      monthKeys.push(`${current.getFullYear()}-${String(current.getMonth()+1).padStart(2,'0')}`);
    }
    const months=monthKeys.map(key=>{
      const [year,month]=key.split('-').map(Number);
      return new Date(year,month-1,1).toLocaleString('en-IN',{month:'short',year:'2-digit'});
    });
    const revByMonth=Object.fromEntries(monthKeys.map(key=>[key,0]));
    const studByMonth=Object.fromEntries(monthKeys.map(key=>[key,0]));
    const monthKeyFor=value=>{
      const date=new Date(value);
      return Number.isNaN(date.getTime()) ? '' : `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}`;
    };

    if(completeData) filtered.forEach(p=>{
      const dateStr = p.payment_date || p.created_at || p.date || p.subscription_start;
      if(!dateStr) return;
      const key=monthKeyFor(dateStr);
      if(Object.prototype.hasOwnProperty.call(revByMonth,key)){
        revByMonth[key] += Number(p.amount || p.total_amount || p.revenue || 0);
        studByMonth[key] += 1;
      }
    });

    // For student report type, use student count per month from API data
    if(reportType==='students' || reportType==='subscriptions'){
      if(completeData) filtered.forEach(p=>{
        const dateStr = p.created_at || p.joining_date || p.start_date;
        if(!dateStr) return;
        const key=monthKeyFor(dateStr);
        if(Object.prototype.hasOwnProperty.call(studByMonth,key)) studByMonth[key] += 1;
      });
    }

    // if summary has monthly breakdown from backend, use it
    if(reportSummary.monthly_revenue){
      Object.entries(reportSummary.monthly_revenue).forEach(([date,value])=>{ const key=monthKeyFor(date); if(Object.prototype.hasOwnProperty.call(revByMonth,key)&&Number.isFinite(Number(value))) revByMonth[key]=Number(value); });
    }
    if(reportSummary.monthly_students){
      Object.entries(reportSummary.monthly_students).forEach(([date,value])=>{ const key=monthKeyFor(date); if(Object.prototype.hasOwnProperty.call(studByMonth,key)&&Number.isFinite(Number(value))) studByMonth[key]=Number(value); });
    }

    const revVals = monthKeys.map(key=>revByMonth[key]||0);
    const studVals = monthKeys.map(key=>studByMonth[key]||0);

    // Library list from real libs
    const libMap = libs.map(l=>{
      const name=l.name||l.library_name||'Unknown';
      const libraryId=String(l.library_id||l.id||l._id||'');
      const belongsToLibrary=record=>{
        const recordLibraryId=record.library_id||record.libraryId||record.libraryid||record.library?.library_id||record.library?.id||record.library?._id;
        if(recordLibraryId!=null) return String(recordLibraryId)===libraryId;
        const recordLibraryName=record.library_name||record.libraryName||record.library?.library_name||record.library?.name;
        return Boolean(recordLibraryName)&&String(recordLibraryName).toLowerCase()===name.toLowerCase();
      };
      const libraryRooms=rooms.filter(belongsToLibrary);
      const roomIds=new Set(libraryRooms.map(room=>String(room.room_id||room.roomid||room.roomId||room.id||room._id||'')));
      const roomsWithIds=new Set([...roomIds].filter(Boolean));
      const roomSeats=libraryRooms.reduce((total,room)=>total+Number(room.total_seat_count||room.total_seats||room.totalseats||room.capacity||0),0);
      const seatCount=roomSeats || seats.filter(seat=>{
        const seatRoomId=seat.room_id||seat.roomid||seat.roomId;
        return belongsToLibrary(seat)||(seatRoomId&&roomsWithIds.has(String(seatRoomId)));
      }).length;
      const studentsInLibrary=students.filter(belongsToLibrary).length;
      const occupancy=seatCount ? Math.round(studentsInLibrary/seatCount*100) : null;
      return {id:libraryId||name,name,students:studentsInLibrary,rooms:libraryRooms.length,seats:seatCount,occupancy};
    });

    return {
      revenueData:{ labels:months, values:revVals },
      studentData:{ labels:months, values:studVals },
      libraryList: libMap.length?libMap:[] // NO DUMMY FALLBACK
    };
  },[filtered, libs, rooms, seats, students, filters.start, filters.end, reportSummary, reportType,pagination.totalRecords,rawPayments.length]);

  const formatSummaryLabel = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, l=>l.toUpperCase());
  const formatSummaryValue = (key, value) => {
    if (value === null || value === undefined || typeof value === 'object') return null;
    const moneyKeys = new Set(['total_revenue','avg_transaction','total_amount','outstanding_amount','total_outstanding','total_paid','amount','amount_due','total_due','subscription_amount_due','seat_upgrade_due','carried_over_due','revenue']);
    if (moneyKeys.has(key) && Number.isFinite(Number(value))) return new Intl.NumberFormat('en-IN',{style:'currency',currency:'INR',maximumFractionDigits:2}).format(Number(value));
    if (key === 'occupancy_rate' && Number.isFinite(Number(value))) return `${Number(value).toFixed(1)}%`;
    return typeof value === 'number' ? value.toLocaleString('en-IN') : String(value);
  };

  const reportCards = useMemo(() => {
    const rows = filtered;
    const completeData=pagination.totalRecords<=rawPayments.length;
    const sum = (keys) => rows.reduce((total, row) => {
      const value = keys.map(key => row?.[key]).find(v => v !== undefined && v !== null && v !== '');
      const number = Number(value); return total + (Number.isFinite(number) ? number : 0);
    }, 0);
    const countStatus = (...statuses) => completeData ? rows.filter(row => statuses.includes(String(row.status || row.subscription_status || row.payment_status || '').toLowerCase())).length : null;
    const countUnique = (keys) => completeData ? new Set(rows.map(row => { const value = keys.map(key => row?.[key]).find(v => v !== undefined && v !== null && v !== ''); return value == null ? null : String(value); }).filter(Boolean)).size : null;
    const firstNumber = (keys, fallback) => {
      for (const key of keys) {
        const value = reportSummary?.[key];
        if (value !== undefined && value !== null && value !== '' && Number.isFinite(Number(value))) return Number(value);
      }
      return fallback;
    };
    const rowAmount = completeData ? sum(['amount','total_amount','revenue','payment_amount','amount_paid']) : null;
    const totalRows = completeData ? rows.length : null;
    const summary = reportSummary || {};
    const cardsByType = {
      revenue: [
        ['total_revenue', firstNumber(['total_revenue','revenue','total_amount'], rowAmount)],
        ['total_transactions', firstNumber(['total_transactions','total_payments','total'], totalRows)],
        ['unique_students', firstNumber(['unique_students'], countUnique(['student_id','studentId','student_name','user_id']))],
        ['avg_transaction', firstNumber(['avg_transaction'], rowAmount!==null && totalRows ? rowAmount/totalRows : null)],
      ],
      occupancy: [
        ['total_seats', firstNumber(['total_seats','total'], seats.length || sum(['total_seats','seats','capacity']))],
        ['occupied', firstNumber(['occupied'], sum(['occupied','occupied_seats']))],
        ['available', firstNumber(['available'], sum(['available','available_seats']))],
        ['occupancy_rate', firstNumber(['occupancy_rate'], (() => { const total = firstNumber(['total_seats','total'], seats.length); const occupied = firstNumber(['occupied'], sum(['occupied','occupied_seats'])); return total ? occupied/total*100 : 0; })())]
      ],
      subscriptions: [
        ['total', firstNumber(['total','total_subscriptions'], totalRows)],
        ['active', firstNumber(['active','active_subscriptions'], countStatus('active'))],
        ['expired', firstNumber(['expired','expired_subscriptions'], countStatus('expired'))],
        ['expiring_soon', firstNumber(['expiring_soon'], countStatus('expiring soon','expiring_soon'))],
      ],
      payments: [
        ['total_payments', firstNumber(['total_payments','total_transactions','total'], totalRows)],
        ['completed_payments', firstNumber(['completed_payments','total_paid'], countStatus('paid','completed','success'))],
        ['pending_payments', firstNumber(['pending_payments'], countStatus('pending'))],
        ['total_amount', firstNumber(['total_amount','total_revenue'], rowAmount)],
      ],
      students: [
        ['total_students', firstNumber(['total_students','total'], totalRows || countUnique(['student_id','studentId','student_name']))],
        ['active_students', firstNumber(['active_students','active'], countStatus('active'))],
        ['inactive_students', firstNumber(['inactive_students','inactive'], countStatus('inactive'))],
        ['total_outstanding', firstNumber(['total_outstanding','outstanding_amount','total_due'], sum(['amount_due','outstanding_amount','total_due']))]
      ]
    };
    return (cardsByType[reportType] || cardsByType.revenue).map(([key, fallback]) => ({
      key, value: summary[key] !== undefined && summary[key] !== null ? summary[key] : fallback
    }));
  }, [reportType, reportSummary, filtered, seats, pagination.totalRecords, rawPayments.length]);

  const tableHeaders=useMemo(()=>{
    const keys=reportColumns.length ? reportColumns : Object.keys(rawPayments[0]||{});
    return keys.filter(key=>!hiddenColumns.includes(key) && !(reportType==='revenue' && key==='payment_id'));
  },[reportColumns,rawPayments,hiddenColumns,reportType]);
  const formatCell=(key,value)=>{
    if(value===null||value===undefined||value==='') return '-';
    if(key.toLowerCase().includes('date')||key.toLowerCase().endsWith('_at')){
      const date=new Date(value);
      return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString('en-IN');
    }
    const moneyKeys=['amount','revenue','total_amount','amount_due','outstanding_amount','total_paid','total_due','payment_amount'];
    if(moneyKeys.includes(key)&&Number.isFinite(Number(value))) return new Intl.NumberFormat('en-IN',{style:'currency',currency:'INR',maximumFractionDigits:2}).format(Number(value));
    return typeof value==='number' ? value.toLocaleString('en-IN') : String(value);
  };
  const totalRecords=pagination.totalRecords;
  const totalPages=Math.max(1,pagination.totalPages);
  const visiblePageCount=Math.min(totalPages,5);
  const firstVisiblePage=Math.max(1,Math.min(currentPage-2,totalPages-visiblePageCount+1));
  const visiblePages=Array.from({length:visiblePageCount},(_,index)=>firstVisiblePage+index);

  return(
    <main className="reports-page">
      <header className="reports-page-heading">
        <div className="reports-heading-copy">
          <span className="reports-heading-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none"><path d="M4 19V5m0 14h17M7 15l4-4 3 2 6-7" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/><path d="M16 6h4v4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </span>
          <div><h1>Reports</h1><p>Explore revenue, occupancy, subscriptions, and student activity.</p></div>
        </div>
      </header>
      <div className="filter-toggle-row">
        <button type="button" className="filter-toggle-btn" aria-expanded={filtersOpen} aria-controls="reports-filter-panel" onClick={()=>setFiltersOpen(previous=>!previous)}>
          <FaFilter aria-hidden="true" /><span>{filtersOpen ? 'Hide filters' : 'Show filters'}</span>
        </button>
      </div>
      {filtersOpen && <div id="reports-filter-panel" className="panel report-filter report-filter-advanced is-open">
        <div className="report-filter-grid">
          <label className="report-filter-field">Report Type
            <UiSelect value={reportType} onChange={e=>{setReportType(e.target.value);setCurrentPage(1);}}>
              {reportTypes.map(type=><option key={type.value} value={type.value}>{type.label||type.name||type.value}</option>)}
            </UiSelect>
          </label>
          <label className="report-filter-field">Search
            <input type="search" placeholder="Search report data..." value={search} onChange={e=>{setSearch(e.target.value);setCurrentPage(1);}} />
          </label>
          <label className="report-filter-field">Library
            <UiSelect value={filters.library} onChange={e=>{setFilters(prev=>({...prev,library:e.target.value}));setCurrentPage(1);}}>
              <option value="">All Libraries</option>
              {libs.map(library=>{
                const id=library.library_id||library.id||library._id;
                const name=library.library_name||library.name||'Library';
                return <option key={id} value={id}>{name}{library.is_deleted?' (deleted)':''}</option>;
              })}
            </UiSelect>
          </label>
          {statusOptions.length>0 && <label className="report-filter-field">Status
            <UiSelect value={statusFilter} onChange={e=>{setStatusFilter(e.target.value);setCurrentPage(1);}}>
              <option value="">All Statuses</option>
              {statusOptions.map(option=><option key={option} value={option}>{String(option).replace(/_/g,' ').replace(/\b\w/g,char=>char.toUpperCase())}</option>)}
            </UiSelect>
          </label>}
          {sortOptions.length>0 && <>
            <label className="report-filter-field">Sort By
              <UiSelect value={sortKey} onChange={e=>{setSortKey(e.target.value);setCurrentPage(1);}}>
                <option value="">Default</option>
                {sortOptions.map(option=><option key={option} value={option}>{String(option).replace(/_/g,' ').replace(/\b\w/g,char=>char.toUpperCase())}</option>)}
              </UiSelect>
            </label>
            <label className="report-filter-field">Order
              <UiSelect value={sortOrder} onChange={e=>{setSortOrder(e.target.value);setCurrentPage(1);}} disabled={!sortKey}>
                <option value="desc">Descending</option><option value="asc">Ascending</option>
              </UiSelect>
            </label>
          </>}
          {(requiresDates||dateOptional) && <>
            <label className="report-filter-field">Start Date
              <input type="date" value={filters.start} max={filters.end || undefined} onChange={e=>{setRangeMode('');setFilters(prev=>({...prev,start:e.target.value}));setCurrentPage(1);}} />
            </label>
            <label className="report-filter-field">End Date
              <input type="date" value={filters.end} min={filters.start || undefined} max={new Date().toISOString().split('T')[0]} onChange={e=>{setRangeMode('');setFilters(prev=>({...prev,end:e.target.value}));setCurrentPage(1);}} />
            </label>
            <label className="report-filter-field">Quick Range
              <UiSelect value={rangeMode} onChange={e=>{applyQuickRange(e.target.value);setCurrentPage(1);}}>
                {dateOptional&&<option value="all">All Time</option>}
                <option value="">Custom Range</option>
                <option value="7">Last 7 days</option><option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option><option value="180">Last 6 months</option>
                <option value="365">Last year</option>
              </UiSelect>
            </label>
          </>}
          <div className="report-filter-actions">
            <button className="button primary" onClick={handleGenerate} disabled={generating||loading}>{generating||loading ? 'Generating...' : 'Generate report'}</button>
            <button className="button secondary export-btn" onClick={()=>setShowExport(true)}><svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 3v12m0 0 4-4m-4 4-4-4M5 17v3h14v-3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>Export</button>
            <button className="button secondary" onClick={()=>{
              setSearch(''); setStatusFilter(''); setSortKey(''); setSortOrder('desc'); setReportType('revenue'); setRangeMode('all');setCurrentPage(1);
              setFilters({ library:'',start:'',end:'' });
            }}>Clear Filters</button>
          </div>
        </div>
      </div>}

      {reportError && <div className="reports-error" role="alert"><span>{reportError}</span><button type="button" onClick={handleGenerate} disabled={generating}>Try again</button></div>}
      {loading ? <ReportsSkeleton /> : <>
      <div className="report-summary-cards" aria-label={`${reportType} report summary`}>
        {reportCards.map(({ key, value }) => {
          const displayValue = formatSummaryValue(key, value);
          return <div className="report-summary-card" key={key}><div className="report-summary-card-label">{formatSummaryLabel(key)}</div><div className="report-summary-card-value">{displayValue ?? '—'}</div></div>;
        })}
      </div>

      <div className="report-line-grid">
        <FigmaLineChart title={reportType==='revenue'?'Revenue Trend':reportType==='students'?'Student Growth':`${formatSummaryLabel(reportType)} Trend`} labels={revenueData.labels} values={reportType==='students'||reportType==='subscriptions'?studentData.values:revenueData.values} type={reportType} />
        <FigmaLineChart title={reportType==='revenue'?'Transactions Count':'Student Growth'} labels={studentData.labels} values={studentData.values} type="students" />
      </div>

      <section className="reports-data-panel">
        <div className="reports-data-heading">
          <div><h2>{activeSpec?.label || `${formatSummaryLabel(reportType)} Report`}</h2><p>{totalRecords.toLocaleString('en-IN')} records matching the selected filters</p></div>
          <label className="reports-rows-control">Rows per page
            <UiSelect value={String(pageSize)} onChange={handlePageSizeChange}>
              {[10,25,50,100].map(size=><option key={size} value={size}>{size}</option>)}
            </UiSelect>
          </label>
        </div>
        {rawPayments.length ? <div className="reports-table-scroll">
          <table className="reports-data-table">
            <thead><tr>{tableHeaders.map(key=><th key={key}>{String(key).replace(/_/g,' ')}</th>)}</tr></thead>
            <tbody>{rawPayments.map((row,index)=><tr key={row.payment_id||row.student_id||row.id||row._id||index}>
              {tableHeaders.map(key=>{
                const value=row[key];
                const status=key==='status'?String(value||'').toLowerCase():'';
                const tone=['active','completed','available','paid','success'].includes(status)?'ok':['pending','reserved','expiring soon','expiring_soon'].includes(status)?'warn':['expired','due','failed','inactive'].includes(status)?'bad':'';
                return <td key={key}>{tone?<span className={`reports-status-pill status-${tone}`}>{String(value)}</span>:formatCell(key,value)}</td>;
              })}
            </tr>)}</tbody>
          </table>
        </div> : <div className="reports-empty-state">{reportError ? 'Report data could not be loaded.' : 'No records match the selected filters.'}</div>}
        <footer className="reports-pagination">
          <span>{totalRecords ? `Showing ${(currentPage-1)*pageSize+1}–${Math.min(currentPage*pageSize,totalRecords)} of ${totalRecords.toLocaleString('en-IN')} records` : '0 records'}</span>
          <nav aria-label="Report pages">
            <button type="button" onClick={()=>handlePageChange(currentPage-1)} disabled={currentPage<=1||generating} aria-label="Previous page">‹</button>
            {visiblePages.map(page=><button type="button" key={page} className={page===currentPage?'active':''} onClick={()=>handlePageChange(page)} disabled={generating} aria-current={page===currentPage?'page':undefined}>{page}</button>)}
            <button type="button" onClick={()=>handlePageChange(currentPage+1)} disabled={currentPage>=totalPages||generating} aria-label="Next page">›</button>
          </nav>
        </footer>
      </section>

      <section className="panel library-performance">
        <div className="panel-heading"><div><h2>Library Performance</h2><p>Current snapshot · all libraries, independent of report filters</p></div></div>
        {librarySnapshotLoading ? <div className="library-snapshot-message">Loading current library metrics…</div>
          : librarySnapshotError ? <div className="library-snapshot-message error">Unable to load a complete library snapshot. Refresh and try again.</div>
          : libraryList.length ? <FigmaBarChart libraries={libraryList} />
          : <div className="library-snapshot-message">No library data is available.</div>}
      </section>

      <section className="panel library-summary">
        <div className="panel-heading"><div><h2>Library Summary</h2><p>Current snapshot · all {libraryList.length} libraries</p></div></div>
        <table className="lib-table">
          <thead><tr><th>Library</th><th>Students</th><th>Rooms</th><th>Seats</th><th>Occupancy</th></tr></thead>
          <tbody>{librarySnapshotLoading
            ? <tr><td colSpan="5">Loading current library metrics…</td></tr>
            : librarySnapshotError
              ? <tr><td colSpan="5">Unable to load a complete library snapshot.</td></tr>
              : libraryList.map(r=><tr key={r.id}><td>{r.name}</td><td>{r.students}</td><td>{r.rooms}</td><td>{r.seats}</td><td>{r.occupancy===null?'—':`${r.occupancy}%`}</td></tr>)}
          </tbody>
        </table>
      </section>
      </>}

      {showExport && (
        <div className="export-overlay" onClick={()=>setShowExport(false)}>
          <div className="export-popup" onClick={e=>e.stopPropagation()}>
            <div className="export-popup-head"><h3>Export Report</h3><button className="close-x" aria-label="Close export dialog" onClick={()=>setShowExport(false)}>×</button></div>
            <div className="export-options">
              <button className="export-option" onClick={()=>handleExport('csv')}><span className="exp-ic" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none"><path d="M6 3h8l4 4v14H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Zm8 0v5h5M8 12h8m-8 4h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg></span><div><b>CSV</b><small>Comma-separated values</small></div></button>
              <button className="export-option" onClick={()=>handleExport('xlsx')}><span className="exp-ic" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none"><path d="M4 19V5m0 14h17M7 15l4-4 3 2 6-7" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg></span><div><b>XLSX</b><small>Excel spreadsheet</small></div></button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

function FigmaLineChart({ title, labels, values, type }){
  const W=340, H=190, P={top:10,right:10,bottom:28,left:40};
  const max = Math.max(...values,1);
  const min = Math.min(...values)*0.3;
  const points = values.map((v,i)=>{
    const x = P.left + (i/(values.length-1||1))*(W-P.left-P.right);
    const y = P.top + (1-(v-min)/(max-min||1))*(H-P.top-P.bottom);
    return {x,y,v};
  });
  let d = points.length?`M ${points[0].x} ${points[0].y}`:'';
  for(let i=1;i<points.length;i++){
    const prev = points[i-1]; const curr = points[i]; const cx = (prev.x+curr.x)/2;
    d+= ` C ${cx} ${prev.y}, ${cx} ${curr.y}, ${curr.x} ${curr.y}`;
  }
  const areaD = points.length?`${d} L ${points[points.length-1].x} ${H-P.bottom} L ${points[0].x} ${H-P.bottom} Z`:'';
  const [activeIdx, setActiveIdx] = useState(null);
  const [showTip, setShowTip] = useState(false);

  const handleMove = (e) => {
    if(!points.length) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const relX = ((e.clientX - rect.left) / rect.width) * W;
    let closest = 0; let minDist = Infinity;
    points.forEach((p,i)=>{ const dist = Math.abs(p.x - relX); if(dist < minDist){ minDist = dist; closest = i; } });
    setActiveIdx(closest); setShowTip(true);
  };
  const handleLeave = () => { setShowTip(false); setActiveIdx(null); };
  const current = activeIdx !== null ? points[activeIdx] : null;
  const hasData = values.some(v=>v>0);

  return(
    <div className="line-chart-panel">
      <h3>{title}</h3>
      <div className="chart-svg-wrap" onMouseMove={handleMove} onMouseLeave={handleLeave} onMouseEnter={()=>setShowTip(true)}>
        {!hasData && <div className="chart-empty">No data for selected period</div>}
        <svg viewBox={`0 0 ${W} ${H}`}>
          <defs><linearGradient id={`grad-${type}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#6366f1" stopOpacity="0.18"/><stop offset="100%" stopColor="#6366f1" stopOpacity="0"/></linearGradient></defs>
          {[0,1,2,3].map(i=>{
            const y = P.top + i*(H-P.top-P.bottom)/4;
            return <g key={i}><line x1={P.left} x2={W-P.right} y1={y} y2={y} stroke="#f3f4f6" strokeWidth="1" /><text x={P.left-8} y={y+3} fontSize="9" fill="#9ca3af" textAnchor="end">{type==='revenue'?`₹${Math.round((max-(i*max/4))/1000)}k`:Math.round(max-(i*max/4))}</text></g>
          })}
          {hasData && <><path d={areaD} fill={`url(#grad-${type})`} /><path d={d} fill="none" stroke="#6366f1" strokeWidth="2.2" strokeLinecap="round" /></>}
          {points.map((p,i)=>(
            <g key={i}>
              <circle cx={p.x} cy={p.y} r={activeIdx===i?6:0} fill="#fff" stroke="#6366f1" strokeWidth="2" />
              <circle cx={p.x} cy={p.y} r={activeIdx===i?3.5:3.2} fill={activeIdx===i?"#6366f1":"#fff"} stroke="#6366f1" strokeWidth="1.6" />
              {activeIdx===i && showTip && <line x1={p.x} x2={p.x} y1={P.top} y2={H-P.bottom} stroke="#e5e7eb" strokeDasharray="4 4" />}
            </g>
          ))}
        </svg>
        {showTip && current && activeIdx!==null && (
          <div className="chart-tooltip" style={{left: `${current.x}px`, top:`${current.y - 12}px`, transform:'translate(-50%, -100%)'}}>
            <b>{labels[activeIdx]}</b>
            {type==='revenue' || type==='payments' ? <small>Revenue: {new Intl.NumberFormat('en-IN',{style:'currency',currency:'INR',maximumFractionDigits:0}).format(values[activeIdx])}</small> : <small>Count: {values[activeIdx]}</small>}
          </div>
        )}
      </div>
      <div className="chart-x-labels">{labels.map((l,i)=><span key={i} className={activeIdx===i?'active-x':''}>{l}</span>)}</div>
    </div>
  );
}

function FigmaBarChart({ libraries }){
  return(
    <div className="performance-chart">
      <div className="performance-axis-labels">{libraries.map(l=> <span key={l.id}>{l.name}</span>)}</div>
      <div className="performance-plot">
        <div className="performance-gridlines"><i/><i/><i/><i/><i/></div>
        {libraries.map(l=>(
          <div className="performance-row" key={l.id}>
            <div className="performance-bar occupancy-bar" style={{width:`${l.occupancy??0}%`}} title={l.occupancy===null?'Seat capacity unavailable':`${l.occupancy}% occupancy`}></div>
          </div>
        ))}
        <div className="performance-ticks"><span>0%</span><span>25%</span><span>50%</span><span>75%</span><span>100%</span></div>
        <div className="performance-legend"><span><i></i>Occupancy (students ÷ seats)</span></div>
      </div>
    </div>
  );
}
